import React, { ReactNode } from 'react';

import { ISourceSet, sourceSetIds } from '../../../../state/types';

import {
    useGridCreatorLevel,
    useIsAnalyticsModeEnabled,
    useIsCalibrationDataChartPaneVisible,
    useIsChartPaneVisible,
    useIsDataGridPaneVisible,
    useIsDetectionChartPaneVisible,
    usePreviewAction,
    usePreviewPaneId,
    useSnapshotActionsToTrigger,
    useTimeLocked,
} from '../../selectors/index.hooks';
import { useGridSourceSet } from '../../selectors/composed/index.hooks';

import CalibrationPane from '../CalibrationPane';
import ChartPane from '../ChartPane';
import DataGridPane from '../DataGridPane';
import RoutesLeftHeader from '../DataGridPane/components/RoutesLeftHeader';
import TasksLeftHeader from '../DataGridPane/components/TasksLeftHeader';
import TasksPreviewLeftHeader from '../DataGridPane/components/TasksPreviewLeftHeader';
import DetectionPane from '../DetectionPane';
import { useAppDispatch } from '../../../../hooks';
import TasksAddButton from '../DataGridPane/components/TasksAddButton/TasksAddButton';
import { useHandleTriggerActions } from '../../hooks/useHandleTriggerActions';
import useDiscoveryLayout from '../../hooks/useDiscoveryLayout';

export interface IDataGridCustomComponents {
    leftHeader?: (
        sourceSet: ISourceSet | null,
        searchPhrase: string,
        onChange: (phrase: string) => void
    ) => ReactNode;
    addButton?: (isGriddFullscreen: boolean) => ReactNode;
}

const DiscoveryGrid = () => {
    const { layoutClassName } = useDiscoveryLayout();

    const dispatch = useAppDispatch();
    const sourceSet = useGridSourceSet();
    const isAnalyticsEnabled = useIsAnalyticsModeEnabled();
    const timeLocked = useTimeLocked();
    const creatorLevel = useGridCreatorLevel();

    const selectedSourceSetElementId = usePreviewPaneId();
    const isChartPaneVisible = useIsChartPaneVisible();
    const isDataGridPaneVisible = useIsDataGridPaneVisible();
    const isDetectionChartPaneVisible = useIsDetectionChartPaneVisible();
    const isCalibrationChartPaneVisible =
        useIsCalibrationDataChartPaneVisible();
    const actionsToTrigger = useSnapshotActionsToTrigger();
    const previewAction = usePreviewAction();

    useHandleTriggerActions({
        sourceSet,
        creatorLevel,
        previewAction,
        actionsToTrigger,
        dispatch,
    });

    const getCustomComponents = (): IDataGridCustomComponents => {
        switch (sourceSet?.definitionId) {
            case sourceSetIds.tasks:
                return {
                    leftHeader: (
                        sourceSet: ISourceSet | null,
                        searchPhrase: string,
                        onChange: (phrase: string) => void
                    ) => (
                        <TasksLeftHeader
                            sourceSet={sourceSet}
                            filtering={{
                                searchPhrase,
                                onChange,
                            }}
                        />
                    ),
                    addButton: (isGridFullscreen: boolean) => {
                        return (
                            <TasksAddButton
                                isGridFullscreen={isGridFullscreen}
                            />
                        );
                    },
                };
            case sourceSetIds.tasksPreview:
                return {
                    leftHeader: (
                        sourceSet: ISourceSet | null,
                        searchPhrase: string,
                        onChange: (phrase: string) => void
                    ) => (
                        <TasksPreviewLeftHeader
                            sourceSet={sourceSet}
                            filtering={{
                                searchPhrase,
                                onChange,
                            }}
                        />
                    ),
                };
            case sourceSetIds.routes:
                return {
                    leftHeader: (
                        sourceSet: ISourceSet | null,
                        searchPhrase: string,
                        onChange: (phrase: string) => void
                    ) => (
                        <RoutesLeftHeader
                            sourceSet={sourceSet}
                            filtering={{
                                searchPhrase,
                                onChange,
                            }}
                        />
                    ),
                };
            default:
                return {};
        }
    };

    return (
        <>
            {isDataGridPaneVisible && (
                <DataGridPane
                    layout={layoutClassName}
                    isAnalyticsEnabled={isAnalyticsEnabled}
                    sourceSet={sourceSet}
                    creatorLevel={creatorLevel}
                    timeLocked={timeLocked}
                    selectedSourceSetElementId={selectedSourceSetElementId}
                    customComponents={getCustomComponents()}
                />
            )}
            {isChartPaneVisible && <ChartPane />}
            {isCalibrationChartPaneVisible && <CalibrationPane />}
            {isDetectionChartPaneVisible && <DetectionPane />}
        </>
    );
};

export default DiscoveryGrid;
